import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import LoginForm from './components/LoginForm';
import TrustSignals from './components/TrustSignals';
import RoleCredentialsGuide from './components/RoleCredentialsGuide';
import BenefitsPanel from './components/BenefitsPanel';

const LoginAuthentication = () => {
  const [selectedRole, setSelectedRole] = useState(null);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (isAuthenticated === 'true') {
      console.log('User already authenticated');
    }
  }, []);

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  return (
    <>
      <Helmet>
        <title>Connexion - PlumberPro | Accès Sécurisé</title>
        <meta 
          name="description" 
          content="Connectez-vous à votre espace PlumberPro. Accès sécurisé pour clients, techniciens, dispatch et administrateurs. Conformité RGPD garantie." 
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <div className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border shadow-sm">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary to-primary/80 shadow-md">
                <Icon name="Droplet" size={24} color="#FFFFFF" />
              </div>
              <span className="text-xl font-semibold text-foreground">
                PlumberPro
              </span>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-success/10 text-success text-xs font-medium rounded-full">
                <span className="w-2 h-2 rounded-full bg-success animate-pulse"></span>
                <span>Service disponible 24/7</span>
              </div>
              <a 
                href="tel:+33123456789"
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                <Icon name="Phone" size={16} />
                <span className="hidden sm:inline">01 23 45 67 89</span>
              </a>
            </div>
          </div>
        </div>

        <main className="pt-24 pb-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <div className="space-y-6">
                <div className="text-center lg:text-left">
                  <h1 className="text-3xl lg:text-4xl font-bold text-foreground mb-3">
                    Bienvenue sur PlumberPro
                  </h1>
                  <p className="text-muted-foreground text-lg">
                    Votre plateforme de gestion de services de plomberie professionnelle
                  </p>
                </div>

                <div className="flex justify-center lg:justify-start">
                  <LoginForm onRoleSelect={handleRoleSelect} />
                </div>

                <div className="lg:hidden">
                  <RoleCredentialsGuide />
                </div>
              </div>

              <div className="space-y-6">
                <div className="hidden lg:block">
                  <RoleCredentialsGuide />
                </div>

                <div className="hidden lg:block">
                  <TrustSignals />
                </div>

                <div className="hidden xl:block">
                  <BenefitsPanel />
                </div>
              </div>
            </div>

            <div className="lg:hidden mt-8 space-y-6">
              <TrustSignals />
              <BenefitsPanel />
            </div>

            <div className="mt-12 bg-card rounded-lg shadow-md p-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="Users" size={24} color="var(--color-primary)" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-1">
                    15 000+
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Clients satisfaits
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="CheckCircle" size={24} color="var(--color-success)" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-1">
                    98%
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Taux de satisfaction
                  </p>
                </div>

                <div className="text-center">
                  <div className="w-12 h-12 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon name="Clock" size={24} color="var(--color-warning)" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-1">
                    &lt; 2h
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Temps d'intervention moyen
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="bg-card border-t border-border mt-12">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-primary to-primary/80">
                    <Icon name="Droplet" size={20} color="#FFFFFF" />
                  </div>
                  <span className="font-semibold text-foreground">PlumberPro</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Votre partenaire de confiance pour tous vos besoins en plomberie.
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-3">Services</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>Dépannage d'urgence</li>
                  <li>Installation sanitaire</li>
                  <li>Rénovation</li>
                  <li>Entretien préventif</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-3">Légal</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li>Mentions légales</li>
                  <li>Politique RGPD</li>
                  <li>CGV</li>
                  <li>Cookies</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-3">Contact</h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Icon name="Phone" size={14} />
                    01 23 45 67 89
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Mail" size={14} />
                    contact@plumberpro.fr
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="MapPin" size={14} />
                    Paris, France
                  </li>
                </ul>
              </div>
            </div>

            <div className="pt-6 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground">
                © {new Date()?.getFullYear()} PlumberPro. Tous droits réservés.
              </p>
              <div className="flex items-center gap-4">
                <span className="text-xs text-muted-foreground">Certifié:</span>
                <div className="flex gap-2">
                  <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded">
                    RGPD
                  </span>
                  <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded">
                    Qualibat
                  </span>
                  <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded">
                    RGE
                  </span>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LoginAuthentication;